import React, { useState, useEffect, useRef } from 'react';
import { FaRobot, FaUser, FaCopy, FaCheck, FaVolumeUp, FaPlay, FaPause, FaRedo } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { speakWithCloud } from '../../utils/cloudSpeech';

const AiMessage = ({ message, userAvatar }) => {
  const [copied, setCopied] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const controllerRef = useRef(null);
  const isUser = message.role === 'user';
  const aiPrefs = useSelector((s) => s.ai?.preferences) || {};

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
    }
  };

  useEffect(() => {
    return () => {
      try {
        const ctrl = controllerRef.current;
        if (ctrl) {
          if (typeof ctrl.cancel === 'function') ctrl.cancel();
          else if (typeof ctrl === 'function') ctrl();
        }
        // Extra safety: also cancel any global speech
        if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      } catch {}
    };
  }, []);

  const startSpeech = (text) => {
    // Cancel any previous controller for this message (cloud or browser)
    try {
      const prev = controllerRef.current;
      if (prev) {
        if (typeof prev.cancel === 'function') prev.cancel();
        else if (typeof prev === 'function') prev();
      }
      // also cancel browser speech as safety for fallback
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      // pause any cloud audio
      const audios = document.querySelectorAll('audio');
      audios.forEach(a => { try { a.pause(); } catch {} });
    } catch {}
    setSpeaking(true);
    setPaused(false);
    // WaveNet cloud first, fallback to browser smartSpeech automatically
    const ctrl = speakWithCloud(text, {
      assistantVoice: aiPrefs.assistantVoice || 'female',
      speechLanguage: aiPrefs.speechLanguage || 'en-IN',
      tone: aiPrefs.tone || 'professional',
      preferCloud: true,
      onEnd: () => {
        setSpeaking(false);
        setPaused(false);
        controllerRef.current = null;
      },
      onError: () => {
        setSpeaking(false);
        setPaused(false);
        controllerRef.current = null;
      },
      onPause: () => setPaused(true),
      onResume: () => setPaused(false),
    });
    controllerRef.current = ctrl;
  };

  const handlePlayPause = () => {
    if (!speaking) {
      startSpeech(message.content);
      return;
    }
    const ctrl = controllerRef.current;
    if (paused) {
      try {
        if (ctrl && typeof ctrl.resume === 'function') ctrl.resume();
        else if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      } catch {}
      setPaused(false);
    } else {
      try {
        if (ctrl && typeof ctrl.pause === 'function') ctrl.pause();
        else if (window.speechSynthesis.speaking) window.speechSynthesis.pause();
      } catch {}
      setPaused(true);
    }
  };

  const handleRepeat = () => {
    // Restart from beginning — works whether idle, playing, or paused (cloud or browser)
    startSpeech(message.content);
  };

  const hasSpeech = true; // cloud TTS available even where speechSynthesis missing

  return (
    <div className={`ai-message ${isUser ? 'ai-message--user' : 'ai-message--assistant'}`}>
      <div className="ai-message__avatar">
        {isUser ? (
          userAvatar ? (
            <img src={userAvatar} alt="Profile" className="ai-message__avatar-img" />
          ) : (
            <FaUser />
          )
        ) : (
          <FaRobot />
        )}
      </div>
      <div className="ai-message__content">
        <div className="ai-message__text">
          {message.content.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line.startsWith('**') && line.endsWith('**') ? (
                <strong>{line.slice(2, -2)}</strong>
              ) : line.includes('**') ? (
                <>
                  {line.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
                    part.startsWith('**') && part.endsWith('**') ? (
                      <strong key={j}>{part.slice(2, -2)}</strong>
                    ) : (
                      <span key={j}>{part}</span>
                    )
                  )}
                </>
              ) : (
                <span>{line}</span>
              )}
              {i < message.content.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
        <div className="ai-message__actions">
          <span className="ai-message__time">
            {message.timestamp
              ? new Date(message.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
              : ''}
          </span>
          {!isUser && hasSpeech && (
            <span className="ai-message__speaker">
              <button
                className={`ai-message__action-btn ai-message__action-btn--speaker ${speaking && !paused ? 'ai-message__action-btn--speaking' : ''} ${paused ? 'ai-message__action-btn--paused' : ''}`}
                onClick={handlePlayPause}
                title={!speaking ? 'Play' : paused ? 'Resume' : 'Pause'}
                aria-label={!speaking ? 'Play speech' : paused ? 'Resume speech' : 'Pause speech'}
              >
                {!speaking ? <FaVolumeUp /> : paused ? <FaPlay /> : <FaPause />}
              </button>
              <button
                className="ai-message__action-btn ai-message__action-btn--repeat"
                onClick={handleRepeat}
                title="Repeat"
                aria-label="Repeat speech"
              >
                <FaRedo />
              </button>
            </span>
          )}
          <button
            className="ai-message__action-btn"
            onClick={() => handleCopy(message.content)}
            title="Copy"
          >
            {copied ? <FaCheck /> : <FaCopy />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiMessage;
